import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Persist collapse state in localStorage
  useEffect(() => {
    const savedCollapseState = localStorage.getItem('sidebar-collapsed');
    if (savedCollapseState !== null) {
      setIsCollapsed(JSON.parse(savedCollapseState));
    }
  }, []);

  // Save collapse state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Modified to implement accordion behavior
  const toggleSubmenu = useCallback((path) => {
    setExpandedMenus(prev => {
      // If clicking the same menu that's already open, just close it
      if (prev[path]) {
        return { [path]: false };
      }
      // Otherwise, close all menus and open the clicked one
      return { [path]: true };
    });
  }, []);

  // Close all expanded menus when navigating to a different section
  const handleNavigation = useCallback((clickedPath) => {
    // Get the top-level path (e.g., "/welcome" from "/welcome/how-we-work")
    const currentTopLevel = location.pathname === '/' ? '/' : '/' + location.pathname.split('/')[1];
    const clickedTopLevel = clickedPath === '/' ? '/' : '/' + clickedPath.split('/')[1];
    
    // Only close menus if navigating to a different top-level section
    if (currentTopLevel !== clickedTopLevel) {
      setExpandedMenus({});
    }
  }, [location.pathname]);

  const navigation = [
    {
      name: 'Home',
      path: '/',
      description: 'Alliance Portal Home',
      icon: 'home'
    },
    {
      name: 'About',
      path: '/about',
      description: 'About the Alliance Partnership Program',
      icon: 'info'
    },
    {
      name: 'Partner Program',
      path: '/partner-program',
      description: 'How the Program Works',
      icon: 'business'
    },
    {
      name: 'Directory',
      path: '/directory',
      description: 'Partner Directory',
      icon: 'people'
    },
    {
      name: 'Expression of Interest',
      path: '/expression-of-interest',
      description: 'Apply for Partnership',
      icon: 'assignment'
    },
    {
      name: 'Contact',
      path: '/contact',
      description: 'Contact Us',
      icon: 'contact_support'
    }
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Don't allow collapse on sign in page
  const isSignInPage = location.pathname === '/signin';
  const actuallyCollapsed = isSignInPage ? false : isCollapsed;

  return (
    <div className={`h-full transition-all duration-300 ${actuallyCollapsed ? 'w-16' : 'w-64'} flex flex-col`} style={{backgroundColor: 'var(--ph-light-grey)', color: 'var(--ph-dark-grey)'}}>
      {/* Collapse Toggle */}
      <div className="p-4 border-b flex-shrink-0" style={{borderColor: 'var(--ph-dark-grey)'}}>
        <div className="flex items-center justify-between">
          <div className={`transition-all duration-300 ${actuallyCollapsed ? 'w-0 overflow-hidden' : 'w-auto'}`}>
            <h2 className="text-lg font-bold" style={{color: 'var(--ph-dark-grey)'}}>
              {isAuthenticated ? 'Navigation' : 'Welcome'}
            </h2>
          </div>
          <button
            onClick={() => setIsCollapsed(prev => !prev)}
            className="p-2 rounded-lg transition-colors ph-sidebar-toggle flex-shrink-0"
            style={{color: 'var(--ph-dark-grey)'}}
          >
            <span className="material-icons">
              {isCollapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>
      </div>

      {/* Navigation with scrollable area */}
      <nav className="flex-1 py-4 overflow-y-auto ph-sidebar-scroll">
        {isAuthenticated ? (
          <ul className="space-y-2 px-3">
            {navigation.map((item) => (
              <li key={item.path} className="relative">
                <div>
                  <Link
                    to={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex items-center px-3 py-3 rounded-lg transition-colors group ph-sidebar-link ${
                      isActive(item.path)
                        ? 'text-white'
                        : ''
                    }`}
                    style={isActive(item.path) ? {backgroundColor: 'var(--ph-dark-grey)', color: 'white'} : {color: 'var(--ph-dark-grey)'}}
                    onMouseEnter={() => setHoveredItem(actuallyCollapsed ? item.path : null)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {/* Simple img approach for SVGs */}
                    {item.icon.startsWith && item.icon.startsWith('/') ? (
                      <img 
                        src={item.icon} 
                        alt={`${item.name} icon`}
                        className="w-5 h-5 mr-3 flex-shrink-0 object-contain"
                      />
                    ) : (
                      <span className="material-icons text-lg mr-3 flex-shrink-0">
                        {item.icon}
                      </span>
                    )}
                    
                    <div className={`transition-all duration-300 ${actuallyCollapsed ? 'w-0 overflow-hidden' : 'w-auto'}`}>
                      <div className="flex-1">
                        <div className="font-semibold">{item.name}</div>
                        <div className="text-xs font-medium opacity-90 group-hover:opacity-100" style={{color: 'inherit'}}>
                          {item.description}
                        </div>
                      </div>
                      
                      {/* Submenu toggle button */}
                      {item.children && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation(); // Prevent the Link onClick from firing
                            toggleSubmenu(item.path);
                          }}
                          className="ml-2 p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors flex-shrink-0"
                        >
                          <span className="material-icons text-sm">
                            {expandedMenus[item.path] ? 'expand_less' : 'expand_more'}
                          </span>
                        </button>
                      )}
                    </div>
                  </Link>
                  
                  {/* Sub-navigation for items with children */}
                  {item.children && (
                    <div className={`transition-all duration-300 ${!isCollapsed && expandedMenus[item.path] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                      <ul className="ml-8 mt-2 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <Link
                              to={child.path}
                              onClick={() => handleNavigation(child.path)}
                              className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm ph-sidebar-link ${
                                isActive(child.path)
                                  ? 'text-white'
                                  : ''
                              }`}
                              style={isActive(child.path) ? {backgroundColor: 'var(--ph-dark-grey)', color: 'white'} : {color: 'var(--ph-dark-grey)'}}
                            >
                              <span className="material-icons text-sm mr-2 flex-shrink-0">
                                {child.icon}
                              </span>
                              <div className="flex-1">
                                <div className="font-semibold">{child.name}</div>
                                <div className="text-xs font-medium opacity-90" style={{color: 'inherit'}}>
                                  {child.description}
                                </div>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Hover tooltip for collapsed sidebar */}
                {actuallyCollapsed && hoveredItem === item.path && (
                  <div className="absolute left-full top-0 ml-2 z-50 bg-white border rounded-lg shadow-lg py-2 min-w-60"
                       style={{color: 'var(--ph-dark-grey)', borderColor: 'var(--ph-light-grey)'}}>
                    <div className="px-4 py-2 border-b" style={{borderColor: 'var(--ph-light-grey)'}}>
                      <div className="font-semibold text-base">{item.name}</div>
                      <div className="text-xs opacity-75">{item.description}</div>
                    </div>
                    
                    {item.children && (
                      <div className="py-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="flex items-center px-4 py-2 hover:bg-gray-50 transition-colors"
                            style={{color: 'var(--ph-dark-grey)'}}
                          >
                            <span className="material-icons text-sm mr-2">
                              {child.icon}
                            </span>
                            <div>
                              <div className="font-medium text-sm">{child.name}</div>
                              <div className="text-xs opacity-75">{child.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-6 py-4 text-center" style={{color: 'var(--ph-dark-grey)'}}>
            <span className="material-icons text-4xl mb-2 block">account_circle</span>
            <p className="text-sm">Please sign in to access navigation</p>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar; 